import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { 
  FiSave, FiInfo, FiChevronDown, FiChevronUp, FiCalendar, FiClock, 
  FiTarget, FiSettings, FiList, FiHelpCircle, FiFilter, FiGrid,
  FiCheckCircle, FiAlertTriangle, FiCheck
} = FiIcons;

const CLOPLOMapping = () => {
  const { state, dispatch, assessmentService, courseService } = useApp();
  const [mapping, setMapping] = useState({});
  const [cloTargets, setCloTargets] = useState({});
  const [ploTargets, setPloTargets] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPLOGuide, setShowPLOGuide] = useState(false);
  const [showTargetSettings, setShowTargetSettings] = useState(false);
  const [showMQFGuide, setShowMQFGuide] = useState(false);
  const [targetsSaving, setTargetsSaving] = useState(false);
  const [targetsSaved, setTargetsSaved] = useState(false);
  const [targetsChanged, setTargetsChanged] = useState(false);
  const [mqfReferences, setMQFReferences] = useState([]);
  const [selectedMQF, setSelectedMQF] = useState('');
  const [mqfMapping, setMQFMapping] = useState({});
  const [mqfSaving, setMQFSaving] = useState(false);
  const [viewMode, setViewMode] = useState('standard'); // 'standard' or 'mqf'
  const [mappingSaved, setMappingSaved] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [activateError, setActivateError] = useState(null);

  const clos = Object.keys(state.cloStatements).length > 0
    ? Object.keys(state.cloStatements)
    : ['CLO1', 'CLO2', 'CLO3'];
  
  const plos = Object.keys(state.ploStatements || {}).length > 0
    ? Object.keys(state.ploStatements)
    : Array.from({ length: 12 }, (_, i) => `PLO${i + 1}`);

  useEffect(() => {
    if (state.activeCourse) {
      loadMappingData();
      loadMQFReferences();
    }
  }, [state.activeCourse]);

  // Load targets from global state when available
  useEffect(() => {
    if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
      console.log('🎯 CLO-PLO Mapping: Loading CLO targets from global state:', state.attainmentTargets.cloTargets);
      setCloTargets(state.attainmentTargets.cloTargets);
    }
    if (state.attainmentTargets.ploTargets && Object.keys(state.attainmentTargets.ploTargets).length > 0) {
      console.log('🎯 CLO-PLO Mapping: Loading PLO targets from global state:', state.attainmentTargets.ploTargets);
      setPloTargets(state.attainmentTargets.ploTargets);
    }
  }, [state.attainmentTargets]);

  const loadMappingData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    setTargetsSaving(false);
    setTargetsSaved(false);
    setTargetsChanged(false);
    setMappingSaved(false);
    try {
      console.log('🔄 Loading mapping data for course:', state.activeCourse.id);
      const [mappingResult, targetsResult, mqfMappingResult] = await Promise.all([
        assessmentService.getCLOPLOMapping(state.activeCourse.id),
        assessmentService.getAttainmentTargets(state.activeCourse.id),
        assessmentService.getCLOPLOMQFMapping(state.activeCourse.id)
      ]);

      if (mappingResult.error) throw new Error(mappingResult.error);

      // Handle mapping data
      if (mappingResult.data && mappingResult.data.length > 0) {
        const mappingObj = mappingResult.data.reduce((acc, item) => {
          if (!acc[item.clo_code]) {
            acc[item.clo_code] = {};
          }
          acc[item.clo_code][item.plo_code] = item.is_mapped;
          return acc;
        }, {});
        setMapping(mappingObj);
        setMappingSaved(true);
      } else {
        // Initialize with default mapping
        const defaultMapping = {};
        clos.forEach(clo => {
          defaultMapping[clo] = {};
          plos.forEach(plo => {
            defaultMapping[clo][plo] = false;
          });
        });
        setMapping(defaultMapping);
        setMappingSaved(false);
      }

      // Handle MQF mapping data
      if (!mqfMappingResult.error && mqfMappingResult.data && mqfMappingResult.data.length > 0) {
        const mqfMappingObj = {};
        mqfMappingResult.data.forEach(item => {
          if (!mqfMappingObj[item.clo_code]) {
            mqfMappingObj[item.clo_code] = {};
          }
          if (!mqfMappingObj[item.clo_code][item.plo_code]) {
            mqfMappingObj[item.clo_code][item.plo_code] = {};
          }
          mqfMappingObj[item.clo_code][item.plo_code][item.mqf_code] = item.is_mapped;
        });
        setMQFMapping(mqfMappingObj);
      } else {
        // Initialize with empty MQF mapping
        const defaultMQFMapping = {};
        clos.forEach(clo => {
          defaultMQFMapping[clo] = {};
          plos.forEach(plo => {
            defaultMQFMapping[clo][plo] = {};
          });
        });
        setMQFMapping(defaultMQFMapping);
      }

      // CRITICAL: Load attainment targets with enhanced handling
      console.log('🎯 Loading targets for course:', state.activeCourse.id);
      console.log('🎯 Target result:', targetsResult);
      if (!targetsResult.error && targetsResult.data) {
        console.log('🎯 Found existing targets:', targetsResult.data);
        const savedCloTargets = targetsResult.data.clo_targets || {};
        const savedPloTargets = targetsResult.data.plo_targets || {};
        console.log('🎯 Loaded CLO targets from DB:', savedCloTargets);
        console.log('🎯 Loaded PLO targets from DB:', savedPloTargets);

        // Build CLO targets - preserve saved values or use defaults
        const cloTargetsToSet = {};
        clos.forEach(clo => {
          cloTargetsToSet[clo] = Object.prototype.hasOwnProperty.call(savedCloTargets, clo)
            ? savedCloTargets[clo]
            : 50;
        });

        // Build PLO targets - preserve saved values or use defaults
        const ploTargetsToSet = {};
        plos.forEach(plo => {
          ploTargetsToSet[plo] = Object.prototype.hasOwnProperty.call(savedPloTargets, plo)
            ? savedPloTargets[plo]
            : 50;
        });

        console.log('🎯 Final CLO targets to set:', cloTargetsToSet);
        console.log('🎯 Final PLO targets to set:', ploTargetsToSet);
        setCloTargets(cloTargetsToSet);
        setPloTargets(ploTargetsToSet);

        // CRITICAL: Update global state immediately with proper dispatch
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: { cloTargets: cloTargetsToSet, ploTargets: ploTargetsToSet }
        });
        setTargetsSaved(true);
        setTargetsChanged(false);
      } else {
        console.log('🎯 No saved targets found, initializing defaults');
        // Initialize with defaults only if no saved data
        const defaultCloTargets = {};
        const defaultPloTargets = {};
        clos.forEach(clo => {
          defaultCloTargets[clo] = 50;
        });
        plos.forEach(plo => {
          defaultPloTargets[plo] = 50;
        });
        setCloTargets(defaultCloTargets);
        setPloTargets(defaultPloTargets);

        // Update global state with defaults
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: { cloTargets: defaultCloTargets, ploTargets: defaultPloTargets }
        });
        setTargetsSaved(false);
        setTargetsChanged(false);
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      console.error('❌ Error loading mapping data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMQFReferences = async () => {
    try {
      const { data, error } = await assessmentService.getMQFReferences();
      if (error) throw new Error(error);
      if (data) {
        setMQFReferences(data);
        if (data.length > 0 && !selectedMQF) {
          setSelectedMQF(data[0].mqf_code);
        }
      }
    } catch (error) {
      console.error('Error loading MQF references:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const toggleMapping = (clo, plo) => {
    setMapping(prev => ({
      ...prev,
      [clo]: { ...prev[clo], [plo]: !prev[clo]?.[plo] }
    }));
    setMappingSaved(false);
  };

  const toggleMQFMapping = (clo, plo, mqfCode) => {
    setMQFMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[clo]) {
        newMapping[clo] = {};
      }
      if (!newMapping[clo][plo]) {
        newMapping[clo][plo] = {};
      }
      
      newMapping[clo][plo][mqfCode] = !newMapping[clo][plo][mqfCode];
      return newMapping;
    });
    setMappingSaved(false);
  };

  // ENHANCED SAVE FUNCTION FOR TARGETS - IMMEDIATE GLOBAL STATE UPDATE
  const saveTargetsManually = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setTargetsSaving(true);
    setTargetsSaved(false);
    try {
      console.log('💾 MANUAL SAVE - CLO Targets:', cloTargets);
      console.log('💾 MANUAL SAVE - PLO Targets:', ploTargets);

      // CRITICAL: Update global state IMMEDIATELY before saving to database
      dispatch({
        type: 'SET_ATTAINMENT_TARGETS',
        payload: { cloTargets, ploTargets }
      });

      // Then save to database
      const { error } = await assessmentService.saveAttainmentTargets(
        state.activeCourse.id,
        { clo_targets: cloTargets, plo_targets: ploTargets }
      );

      if (error) {
        throw new Error(error);
      }

      // Verify save by refreshing from database
      await refreshAttainmentTargets(state.activeCourse.id);
      setTargetsSaved(true);
      setTargetsChanged(false);
      alert('Target attainment values saved successfully!');
      console.log('✅ Targets saved and verified manually');
    } catch (error) {
      console.error('❌ Error saving targets manually:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving targets: ' + error.message);
    } finally {
      setTargetsSaving(false);
    }
  };

  const updateCLOTarget = (clo, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      console.log(`🎯 Updating CLO ${clo} target to ${numValue}%`);
      const newCloTargets = { ...cloTargets, [clo]: numValue };
      setCloTargets(newCloTargets);
      setTargetsChanged(true);
      setTargetsSaved(false);

      // CRITICAL: Update global state immediately for real-time reflection
      dispatch({
        type: 'SET_ATTAINMENT_TARGETS',
        payload: { cloTargets: newCloTargets, ploTargets }
      });
    }
  };

  const updatePLOTarget = (plo, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      console.log(`🎯 Updating PLO ${plo} target to ${numValue}%`);
      const newPloTargets = { ...ploTargets, [plo]: numValue };
      setPloTargets(newPloTargets);
      setTargetsChanged(true);
      setTargetsSaved(false);

      // CRITICAL: Update global state immediately for real-time reflection
      dispatch({
        type: 'SET_ATTAINMENT_TARGETS',
        payload: { cloTargets, ploTargets: newPloTargets }
      });
    }
  };

  const saveMapping = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setLoading(true);
    try {
      console.log('💾 Saving CLO-PLO mapping...');
      
      // Save mapping
      const mappingResult = await assessmentService.saveCLOPLOMapping(
        state.activeCourse.id,
        mapping
      );

      if (mappingResult.error) throw new Error(mappingResult.error);

      // Save MQF mapping
      const mqfMappingResult = await assessmentService.saveCLOPLOMQFMapping(
        state.activeCourse.id,
        mqfMapping
      );

      if (mqfMappingResult.error) throw new Error(mqfMappingResult.error);

      // Update global state
      dispatch({ type: 'SET_PLO_MAPPING', payload: mapping });
      console.log('✅ Successfully saved mapping');
      setMappingSaved(true);
      alert('CLO-PLO mapping saved successfully!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving CLO-PLO mapping: ' + error.message);
      console.error('❌ Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateCourse = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setActivateLoading(true);
    setActivateError(null);
    try {
      console.log('🚀 Activating course:', state.activeCourse.id);
      console.log('Current course status:', state.activeCourse.status);
      
      // Update course status from 'draft' to 'active'
      const { data, error } = await courseService.updateCourse(state.activeCourse.id, {
        status: 'active'
      });

      if (error) {
        console.error('❌ Error activating course:', error);
        throw new Error(error);
      }

      console.log('✅ Course activated successfully:', data);

      // Update course in global state
      dispatch({ type: 'UPDATE_COURSE', payload: data });
      
      // Also update the active course
      dispatch({ type: 'SET_ACTIVE_COURSE', payload: data });

      setShowActivateConfirm(false);
      alert('Course activated successfully! It can now be selected by other users for marks entry.');
    } catch (error) {
      console.error('❌ Error activating course:', error);
      setActivateError(error.message);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error activating course: ' + error.message);
    } finally {
      setActivateLoading(false);
    }
  };

  const refreshAttainmentTargets = async (courseId) => {
    try {
      const { data, error } = await assessmentService.getAttainmentTargets(courseId);
      if (!error && data) {
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: {
            cloTargets: data.clo_targets || {},
            ploTargets: data.plo_targets || {}
          }
        });
      }
    } catch (error) {
      console.error('Error refreshing attainment targets:', error);
    }
  };

  const getMappingStats = () => {
    let totalMappings = 0;
    let activeMappings = 0;
    clos.forEach(clo => {
      plos.forEach(plo => {
        totalMappings++;
        if (mapping[clo]?.[plo]) {
          activeMappings++;
        }
      });
    });

    return { totalMappings, activeMappings };
  };

  const getMQFMappingStats = () => {
    let totalMQFMappings = 0;
    let activeMQFMappings = 0;

    Object.keys(mqfMapping).forEach(clo => {
      Object.keys(mqfMapping[clo] || {}).forEach(plo => {
        if (mapping[clo]?.[plo]) { // Only count MQF mappings for active CLO-PLO pairs
          Object.entries(mqfMapping[clo][plo] || {}).forEach(([mqfCode, isMapped]) => {
            totalMQFMappings++;
            if (isMapped) {
              activeMQFMappings++;
            }
          });
        }
      });
    });

    return { totalMQFMappings, activeMQFMappings };
  };

  const stats = getMappingStats();
  const mqfStats = getMQFMappingStats();

  const getCurrentMQFDescription = () => {
    const mqf = mqfReferences.find(m => m.mqf_code === selectedMQF);
    return mqf ? mqf.description : '';
  };
  
  const getCurrentMQFNotes = () => {
    const mqf = mqfReferences.find(m => m.mqf_code === selectedMQF);
    return mqf ? mqf.notes : '';
  };

  // Check if the course is in draft status (not yet activated)
  const isDraftCourse = state.activeCourse?.status === 'draft';

  // Check if mapping is ready to be activated
  const canActivate = mappingSaved && targetsSaved && stats.activeMappings > 0;

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiInfo} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to work on CLO-PLO mapping.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header - Enhanced */}
      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <h2 className="text-xl font-bold text-purple-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="font-medium text-purple-700">Faculty:</span>
            <p className="text-purple-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-purple-700">Programme:</span>
            <p className="text-purple-600">{state.activeCourse.programme}</p>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-purple-700" />
            <div>
              <span className="font-medium text-purple-700">Semester:</span>
              <p className="text-purple-600">{state.activeCourse.semester}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-purple-700" />
            <div>
              <span className="font-medium text-purple-700">Session:</span>
              <p className="text-purple-600">{state.activeCourse.session}</p>
            </div>
          </div>
          <div>
            <span className="font-medium text-purple-700">Active Mappings:</span>
            <p className="text-purple-600">{stats.activeMappings} / {stats.totalMappings}</p>
          </div>
          <div>
            <span className="font-medium text-purple-700">Coverage:</span>
            <p className="text-purple-600">
              {stats.totalMappings > 0 ? Math.round((stats.activeMappings / stats.totalMappings) * 100) : 0}%
            </p>
          </div>
        </div>
        
        {/* Course Status Indicator */}
        <div className="mt-2 flex items-center">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isDraftCourse 
              ? 'bg-yellow-100 text-yellow-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            <span className={`w-2 h-2 mr-1 rounded-full ${
              isDraftCourse ? 'bg-yellow-400' : 'bg-green-400'
            }`}></span>
            {isDraftCourse ? 'Draft Course' : 'Active Course'}
          </span>
          {isDraftCourse && (
            <span className="ml-2 text-xs text-gray-500">
              This course is in draft mode. Activate it to make it available for marks entry.
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Table 3: CLO-PLO-MQF Mapping Matrix</h2>
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <div className="flex">
              <button
                onClick={() => setViewMode('standard')}
                className={`flex items-center space-x-1 px-3 py-2 ${viewMode === 'standard' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-l-lg hover:bg-purple-700 hover:text-white transition-colors text-sm`}
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
                <span>Standard</span>
              </button>
              <button
                onClick={() => setViewMode('mqf')}
                className={`flex items-center space-x-1 px-3 py-2 ${viewMode === 'mqf' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-r-lg hover:bg-purple-700 hover:text-white transition-colors text-sm`}
              >
                <SafeIcon icon={FiFilter} className="w-4 h-4" />
                <span>MQF View</span>
              </button>
            </div>
            
            <button
              onClick={() => setShowTargetSettings(!showTargetSettings)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiTarget} className="w-4 h-4" />
              <span>Target Settings</span>
              {targetsChanged && (
                <span
                  className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"
                  title="Unsaved changes"
                />
              )}
              {targetsSaved && !targetsChanged && (
                <span className="w-2 h-2 bg-green-400 rounded-full" title="Saved" />
              )}
              <SafeIcon
                icon={showTargetSettings ? FiChevronUp : FiChevronDown}
                className="w-4 h-4"
              />
            </button>

            <button
              onClick={() => setShowPLOGuide(!showPLOGuide)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>PLO Descriptions</span>
              <SafeIcon
                icon={showPLOGuide ? FiChevronUp : FiChevronDown}
                className="w-4 h-4"
              />
            </button>
            
            <button
              onClick={() => setShowMQFGuide(!showMQFGuide)}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
              <span>MQF Guide</span>
              <SafeIcon
                icon={showMQFGuide ? FiChevronUp : FiChevronDown}
                className="w-4 h-4"
              />
            </button>
          </div>
        </div>

        {/* Target Attainment Settings */}
        {showTargetSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
          >
            <h3 className="font-semibold text-green-900 mb-4 flex items-center">
              <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2" /> Target Attainment Settings
              {targetsSaving && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                  <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-1" />
                  Saving...
                </span>
              )}
              {targetsChanged && !targetsSaving && (
                <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  ⚠ Unsaved Changes
                </span>
              )}
              {targetsSaved && !targetsChanged && !targetsSaving && (
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  ✓ Saved
                </span>
              )}
            </h3>
            <p className="text-green-800 text-sm mb-4">
              Set the minimum percentage of students required to achieve each CLO and PLO for the course to be considered successful. These targets will be used in the CLO Analysis and PLO Analysis pages to determine achievement status.
              <strong> Changes are reflected immediately in analysis pages. Click "Save Targets" to persist your changes.</strong>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CLO Targets */}
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3">CLO Target Attainment (%)</h4>
                <div className="space-y-3">
                  {clos.map(clo => (
                    <div key={clo} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <label className="text-sm font-medium text-green-800 w-12">{clo}:</label>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={cloTargets[clo] || 50}
                              onChange={(e) => updateCLOTarget(clo, e.target.value)}
                              disabled={targetsSaving}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                              min="0"
                              max="100"
                              step="1"
                            />
                            <span className="text-sm text-green-700">%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="h-2 rounded-full bg-green-600 transition-all duration-300"
                              style={{ width: `${cloTargets[clo] || 50}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2 bg-green-100 rounded text-xs text-green-800">
                  <strong>Note:</strong> This percentage represents the minimum proportion of students who must achieve the CLO for it to be considered "Achieved" in the CLO Analysis.
                </div>
              </div>

              {/* PLO Targets */}
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3">PLO Target Attainment (%)</h4>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {plos.map(plo => (
                    <div key={plo} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <label className="text-sm font-medium text-green-800 w-12">{plo}:</label>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={ploTargets[plo] || 50}
                              onChange={(e) => updatePLOTarget(plo, e.target.value)}
                              disabled={targetsSaving}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                              min="0"
                              max="100"
                              step="1"
                            />
                            <span className="text-sm text-green-700">%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="h-2 rounded-full bg-green-600 transition-all duration-300"
                              style={{ width: `${ploTargets[plo] || 50}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2 bg-green-100 rounded text-xs text-green-800">
                  <strong>Note:</strong> This percentage represents the minimum proportion of students who must achieve the PLO for it to be considered "Achieved" in the PLO Analysis.
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    const newCloTargets = {};
                    const newPloTargets = {};
                    clos.forEach(clo => newCloTargets[clo] = 50);
                    plos.forEach(plo => newPloTargets[plo] = 50);
                    setCloTargets(newCloTargets);
                    setPloTargets(newPloTargets);
                    setTargetsChanged(true);
                    setTargetsSaved(false);
                    // Update global state for immediate reflection
                    dispatch({
                      type: 'SET_ATTAINMENT_TARGETS',
                      payload: { cloTargets: newCloTargets, ploTargets: newPloTargets }
                    });
                  }}
                  disabled={targetsSaving}
                  className="text-sm px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                >
                  Reset to 50%
                </button>
                <button
                  onClick={() => {
                    const newCloTargets = {};
                    const newPloTargets = {};
                    clos.forEach(clo => newCloTargets[clo] = 70);
                    plos.forEach(plo => newPloTargets[plo] = 70);
                    setCloTargets(newCloTargets);
                    setPloTargets(newPloTargets);
                    setTargetsChanged(true);
                    setTargetsSaved(false);
                    // Update global state for immediate reflection
                    dispatch({
                      type: 'SET_ATTAINMENT_TARGETS',
                      payload: { cloTargets: newCloTargets, ploTargets: newPloTargets }
                    });
                  }}
                  disabled={targetsSaving}
                  className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Set High Standards (70%)
                </button>
                <button
                  onClick={saveTargetsManually}
                  disabled={targetsSaving || !targetsChanged}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {targetsSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                  )}
                  <span>{targetsSaving ? 'Saving...' : 'Save Targets'}</span>
                </button>
              </div>
              <div className="text-sm text-green-700">
                <strong>Average Target:</strong> CLO: {Math.round(Object.values(cloTargets).reduce((sum, val) => sum + (val || 50), 0) / clos.length)}%,
                PLO: {Math.round(Object.values(ploTargets).reduce((sum, val) => sum + (val || 50), 0) / plos.length)}%
              </div>
            </div>
          </motion.div>
        )}

        {/* PLO Descriptions Guide */}
        {showPLOGuide && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <h3 className="font-semibold text-blue-900 mb-3">Programme Learning Outcome (PLO) Descriptions:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
              {Object.entries(state.ploStatements || {}).map(([plo, description]) => (
                <div key={plo} className="bg-white p-3 rounded border border-blue-200">
                  <strong className="text-blue-900">{plo}:</strong>
                  <p className="mt-1 text-blue-700">{description || 'No description available'}</p>
                </div>
              ))}
              {Object.keys(state.ploStatements || {}).length === 0 && (
                <div className="col-span-2 text-center py-4">
                  <p className="text-blue-600">
                    No PLO statements found. Please set up your PLOs first in the "Set PLO" section.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* MQF Guide */}
        {showMQFGuide && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg"
          >
            <h3 className="font-semibold text-orange-900 mb-3">Malaysian Qualifications Framework (MQF) Domains:</h3>
            <p className="text-orange-800 mb-4">
              The Malaysian Qualifications Framework (MQF) is a unified system that classifies qualifications based on a set of criteria agreed nationally and benchmarked against international best practices. The MQF integrates with and links all qualifications in higher education and technical and vocational education and training (TVET).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-orange-800 max-h-96 overflow-y-auto">
              {mqfReferences.map((mqf) => (
                <div key={mqf.mqf_code} className="bg-white p-3 rounded border border-orange-200">
                  <div className="flex justify-between items-center mb-1">
                    <strong className="text-orange-900">{mqf.mqf_code}:</strong>
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                      Domain
                    </span>
                  </div>
                  <p className="font-medium text-orange-800 mb-1">{mqf.description}</p>
                  <p className="text-orange-700 text-xs mt-2">{mqf.notes}</p>
                </div>
              ))}
              {mqfReferences.length === 0 && (
                <div className="col-span-2 text-center py-4">
                  <p className="text-orange-600">
                    Loading MQF references...
                  </p>
                </div>
              )}
            </div>
            <div className="mt-4 p-3 bg-white border border-orange-200 rounded">
              <h4 className="font-semibold text-orange-900 mb-2">How to use MQF in CLO-PLO Mapping</h4>
              <ol className="text-orange-800 space-y-1 list-decimal list-inside">
                <li>Select the MQF domain from the dropdown in the mapping section</li>
                <li>For each CLO-PLO mapping that is active, you can add the associated MQF domain</li>
                <li>This creates a three-dimensional mapping between CLOs, PLOs, and MQF domains</li>
                <li>The mapping helps ensure your curriculum aligns with national qualification standards</li>
                <li>Remember to save your mapping after making changes</li>
              </ol>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <h4 className="font-semibold text-yellow-900 mb-1">MQF Importance</h4>
              <p className="text-yellow-800 text-sm">
                Mapping to MQF domains ensures your programme meets national standards and facilitates international recognition. It also helps with accreditation processes and quality assurance reviews. The MQF serves as a reference point for curriculum design, assessment strategies, and programme outcomes.
              </p>
            </div>
          </motion.div>
        )}

        {/* CLO Statements */}
        {Object.keys(state.cloStatements).length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-3">Course Learning Outcome (CLO) Statements:</h3>
            <div className="space-y-2 text-sm">
              {Object.entries(state.cloStatements).map(([clo, statement]) => (
                <div key={clo} className="bg-white p-3 rounded border border-blue-200">
                  <strong className="text-blue-900">{clo}:</strong>
                  <span className="ml-2 text-blue-700">{statement}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MQF Selector (only in MQF view mode) */}
        {viewMode === 'mqf' && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-2">Selected MQF Domain:</h3>
                <div className="flex items-center gap-3">
                  <select 
                    value={selectedMQF} 
                    onChange={(e) => setSelectedMQF(e.target.value)} 
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 w-full md:w-auto"
                  >
                    {mqfReferences.map(mqf => (
                      <option key={mqf.mqf_code} value={mqf.mqf_code}>
                        {mqf.mqf_code}: {mqf.description}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                    {mqfStats.activeMQFMappings} active mappings
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-orange-800 mb-1">Description:</h4>
                <p className="text-sm text-orange-700">{getCurrentMQFDescription()}</p>
              </div>
            </div>
            <div className="mt-3 p-3 bg-white border border-orange-200 rounded text-sm">
              <h4 className="font-medium text-orange-800 mb-1">Notes:</h4>
              <p className="text-orange-700">{getCurrentMQFNotes()}</p>
            </div>
          </div>
        )}

        {/* Standard View Mapping Matrix */}
        {viewMode === 'standard' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left sticky left-0 bg-gray-50">CLO</th>
                  {plos.map(plo => (
                    <th key={plo} className="border border-gray-300 px-4 py-2 text-center min-w-[80px]">
                      {plo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clos.map(clo => (
                  <tr key={clo}>
                    <td className="border border-gray-300 px-4 py-2 font-medium bg-gray-50 sticky left-0">
                      {clo}
                    </td>
                    {plos.map(plo => (
                      <td key={plo} className="border border-gray-300 px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={mapping[clo]?.[plo] || false}
                          onChange={() => toggleMapping(clo, plo)}
                          className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MQF View Mapping Matrix */}
        {viewMode === 'mqf' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left sticky left-0 bg-gray-50 z-10">CLO</th>
                  {plos.map(plo => (
                    <th key={plo} className="border border-gray-300 px-4 py-2 text-center min-w-[80px]">
                      {plo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clos.map(clo => (
                  <tr key={clo}>
                    <td className="border border-gray-300 px-4 py-2 font-medium bg-gray-50 sticky left-0 z-10">
                      {clo}
                    </td>
                    {plos.map(plo => (
                      <td key={plo} className="border border-gray-300 px-4 py-2 text-center">
                        {/* Only show MQF mapping if CLO-PLO is mapped */}
                        {mapping[clo]?.[plo] ? (
                          <div className="flex flex-col items-center">
                            <div className="mb-1 text-xs text-purple-600">CLO-PLO Mapped</div>
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={mqfMapping[clo]?.[plo]?.[selectedMQF] || false}
                                onChange={() => toggleMQFMapping(clo, plo, selectedMQF)}
                                className="w-5 h-5 text-orange-500 bg-gray-100 border-gray-300 rounded focus:ring-orange-400 focus:ring-2"
                              />
                              <span className="ml-1 text-xs text-orange-700">{selectedMQF}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">Not mapped</div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mapping Statistics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800">Total CLOs</h4>
            <p className="text-2xl font-bold text-blue-600">{clos.length}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800">Total PLOs</h4>
            <p className="text-2xl font-bold text-purple-600">{plos.length}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800">Active Mappings</h4>
            <p className="text-2xl font-bold text-green-600">{stats.activeMappings}</p>
          </div>
        </div>

        {/* MQF Statistics (only show in MQF view) */}
        {viewMode === 'mqf' && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800">Total MQF Domains</h4>
              <p className="text-2xl font-bold text-orange-600">{mqfReferences.length}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-800">Active MQF Mappings</h4>
              <p className="text-2xl font-bold text-amber-600">{mqfStats.activeMQFMappings}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800">Current Domain</h4>
              <p className="font-bold text-yellow-600 text-lg">{selectedMQF}</p>
              <p className="text-xs text-yellow-700 truncate">{getCurrentMQFDescription()}</p>
            </div>
          </div>
        )}

        {/* Save and Activate Buttons */}
        <div className="mt-6 flex justify-end items-center gap-4">
          <button
            onClick={saveMapping}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{loading ? 'Saving...' : 'Save CLO-PLO-MQF Mapping'}</span>
          </button>
          
          {/* Activate Course Button - Only show for draft courses and when mapping is saved */}
          {isDraftCourse && mappingSaved && (
            <button
              onClick={() => setShowActivateConfirm(true)}
              disabled={!canActivate}
              className={`flex items-center space-x-2 px-6 py-2 ${
                canActivate 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-gray-400 cursor-not-allowed'
              } text-white rounded-lg transition-colors`}
              title={!canActivate ? 'Save mapping and target settings first' : 'Activate this course for marks entry'}
            >
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
              <span>Activate Course</span>
            </button>
          )}
        </div>

        {/* Mapping Saved Indicator */}
        {mappingSaved && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-2" />
              <p className="text-green-800 text-sm">
                CLO-PLO-MQF mapping saved successfully.
              </p>
            </div>
          </div>
        )}

        {/* Activation Requirement Alert */}
        {isDraftCourse && !canActivate && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-500 mr-2" />
              <p className="text-yellow-800 text-sm">
                To activate this course, you must save both the CLO-PLO mapping and target settings, and have at least one active mapping.
              </p>
            </div>
          </div>
        )}

        {/* Success Indicator */}
        {targetsSaved && !targetsChanged && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              ✅ Target settings are saved and applied to CLO and PLO Analysis pages.
            </p>
          </div>
        )}

        {/* Unsaved Changes Warning */}
        {targetsChanged && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              ⚠️ You have unsaved changes to target settings. Click "Save Targets" to persist your changes.
            </p>
          </div>
        )}

        {/* Real-time Sync Notice */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">
            🔄 <strong>Real-time Sync:</strong> Changes to target settings are immediately reflected in the CLO Analysis and PLO Analysis pages. Remember to save your changes to persist them permanently.
          </p>
        </div>

        {/* Course Activation Information */}
        {isDraftCourse && (
          <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h4 className="font-semibold text-indigo-900 mb-2">📋 Course Activation:</h4>
            <p className="text-indigo-800 text-sm mb-2">
              This course is currently in <strong>draft mode</strong>. Once you've completed your CLO-PLO mapping and set target values, you can activate the course to make it available for marks entry by other users.
            </p>
            <p className="text-indigo-800 text-sm">
              <strong>Activation requirements:</strong>
              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${mappingSaved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {mappingSaved ? '✓' : '○'} Saved mapping
              </span>
              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${targetsSaved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {targetsSaved ? '✓' : '○'} Saved targets
              </span>
              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stats.activeMappings > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {stats.activeMappings > 0 ? '✓' : '○'} Active mappings
              </span>
            </p>
          </div>
        )}

        {/* MQF Integration Notice */}
        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <h4 className="font-semibold text-orange-900 mb-2">🧩 MQF Integration:</h4>
          <p className="text-orange-800 text-sm mb-2">
            Use the MQF view mode to map Malaysian Qualifications Framework domains to your CLO-PLO mappings. This creates a three-dimensional alignment that ensures your curriculum meets national standards.
          </p>
          <p className="text-orange-800 text-sm">
            <strong>How to use:</strong> First create your CLO-PLO mappings in Standard view, then switch to MQF view to add domain mappings to active CLO-PLO pairs.
          </p>
        </div>

        {/* Recommendations */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-semibold text-yellow-900 mb-2">💡 Mapping Guidelines:</h4>
          <ul className="text-yellow-800 space-y-1 text-sm">
            <li>• Each CLO should be mapped to at least one PLO</li>
            <li>• Consider the learning domains: cognitive, psychomotor, and affective</li>
            <li>• Ensure comprehensive coverage of programme outcomes</li>
            <li>• Review mappings with programme coordinators for alignment</li>
            <li>• Update mappings when course content or PLOs change</li>
            <li>• <strong>Add MQF domains to ensure alignment with national qualification standards</strong></li>
            <li>• <strong>Remember to save target settings to persist changes</strong></li>
            <li>• <strong>Activate your course when ready for marks entry by other users</strong></li>
          </ul>
        </div>
      </div>

      {/* Activate Course Confirmation Modal */}
      {showActivateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Activate Course</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to activate this course? Once activated, the course will be available for selection by other users for marks entry.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> After activation, the course will move from "Draft Courses" to "Active Courses" in the Course Management page.
              </p>
            </div>
            
            {/* Display error message if there was an activation error */}
            {activateError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">
                  <strong>Error:</strong> {activateError}
                </p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowActivateConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={activateCourse}
                disabled={activateLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {activateLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                )}
                <span>{activateLoading ? 'Activating...' : 'Activate'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CLOPLOMapping;